import React, { useState, useEffect } from 'react';
import { FiZap, FiAlertCircle, FiAlertTriangle, FiInfo } from 'react-icons/fi';
import { ownerAnalyticsAPI } from '../../../api/ownerEndpoints';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { showToast } from '../../../components/common/Toaster';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  Section,
  SectionTitle,
  Grid,
  AlertCard,
  LoadingContainer,
  EmptyState,
} from './Analytics.styles';

const Insights = ({ startDate, endDate }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = { 
        start_date: startDate ? `${startDate}T00:00:00Z` : undefined,
        end_date: endDate ? `${endDate}T23:59:59Z` : undefined,
      };
      const response = await ownerAnalyticsAPI.getInsights(params);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'high':
        return theme.colors.error;
      case 'medium':
        return theme.colors.warning;
      case 'low':
      default:
        return theme.colors.info;
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'high':
        return <FiAlertCircle />;
      case 'medium':
        return <FiAlertTriangle />;
      case 'low':
      default:
        return <FiInfo />;
    }
  };

  const getInsightEmoji = (type) => {
    const emojiMap = {
      revenue_drop: '📉',
      low_rating: '⭐',
      high_churn: '👋',
      growth_opportunity: '🚀',
      content_gap: '📚',
    };
    return emojiMap[type] || '💡';
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner size="large" text="Loading insights..." />
      </LoadingContainer>
    );
  }

  if (!data || !data.owner_insights || data.owner_insights.length === 0) {
    return (
      <EmptyState>
        <FiZap size={64} />
        <h3>No Insights Available</h3>
        <p>AI-powered insights will appear here when available</p>
      </EmptyState>
    );
  }

  const insights = data.owner_insights;

  return (
    <>
      <Section>
        <SectionTitle>
          <FiZap />
          Platform Insights & Alerts
        </SectionTitle>
        <Grid minWidth="300px">
          {insights.map((insight, index) => (
            <AlertCard key={index} severity={insight.severity}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: theme.spacing.md }}>
                <span style={{ fontSize: '28px' }}>{getInsightEmoji(insight.type)}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, marginBottom: theme.spacing.xs }}>
                    {getSeverityIcon(insight.severity)}
                    <h4 style={{ margin: 0, color: theme.colors.textPrimary, textTransform: 'capitalize' }}>
                      {insight.type.replace(/_/g, ' ')}
                    </h4>
                    <span style={{ 
                      padding: '4px 12px', 
                      borderRadius: theme.borderRadius.full, 
                      fontSize: theme.fontSizes.xs,
                      fontWeight: theme.fontWeights.semibold,
                      background: getSeverityColor(insight.severity) + '20',
                      color: getSeverityColor(insight.severity),
                      textTransform: 'uppercase',
                    }}>
                      {insight.severity}
                    </span>
                  </div>
                  <p style={{ margin: 0, color: theme.colors.textSecondary, lineHeight: '1.6' }}>
                    {insight.message}
                  </p>
                </div>
              </div>
            </AlertCard>
          ))}
        </Grid>
      </Section>
    </>
  );
};

export default Insights;

